import React, { useState } from "react";
import { Button, message } from "antd";
import { EnvironmentOutlined, ArrowUpOutlined, StarFilled, TagOutlined, EyeOutlined } from "@ant-design/icons";
import { fetchConditionReportPdf } from "../services/api";
import facebookLogo from "../../../assets/images/logos-facebook.svg";
import offerUpLogo from "../../../assets/images/logo-offerup.svg";
import "./BuySignalCard.css";

export default function BuySignalCard({ signal, isSelected, onClick, onViewInsights, onMarkViewed }) {
  const [conditionReportLoading, setConditionReportLoading] = useState(false);
  const getScoreColor = (score) => {
    if (score >= 90) return "green";
    if (score >= 80) return "yellow";
    return "gray";
  };

  const scoreColor = getScoreColor(signal.buyScore);
  
  // Show full price opportunity (Ask + Hooptie + Above Ask) for recommended and underpriced
  const showFullPriceOpportunity = signal.recommended || signal.underpriced;
  const askingPrice = signal.price != null && signal.price !== "" ? Number(signal.price) : null;
  const hooptiePrice = signal.retail != null && signal.retail !== "" ? Number(signal.retail) : null;
  const hasAsking = askingPrice != null && !Number.isNaN(askingPrice);
  const hasHooptie = hooptiePrice != null && !Number.isNaN(hooptiePrice);
  const priceDiff = showFullPriceOpportunity && hasAsking && hasHooptie ? Math.abs(hooptiePrice - askingPrice) : null;
  const askingDisplay = hasAsking ? `$${askingPrice.toLocaleString()}` : "—";
  const hooptieDisplay = showFullPriceOpportunity && hasHooptie ? `$${hooptiePrice.toLocaleString()}` : "—";
  const priceDiffDisplay = priceDiff != null ? `$${priceDiff.toLocaleString()} Above Ask` : "—";

  // Format time ago
  const formatTimeAgo = (listedAt) => {
    if (!listedAt) return "Listed recently";
    
    const now = new Date();
    const listed = new Date(listedAt);
    const diffMs = now - listed;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `Listed ${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `Listed ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else {
      return `Listed ${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    }
  };

  const timeAgo = formatTimeAgo(signal.listedAt);

  return (
    <div
      className={`bs-grid-card ${isSelected ? "bs-card-selected" : ""} ${signal.viewedAt ? "bs-card-viewed" : ""}`}
      onClick={onClick}
    >
      {/* Image Container */}
      <div className="bs-card-image-container">
        {signal.recommended && signal.underpriced && (
          <div className="bs-card-recommended-badge">
            <StarFilled className="bs-recommended-star" />
            <span>RECOMMENDED</span>
          </div>
        )}
        {signal.underpriced && !signal.recommended && (
          <div className="bs-card-underpriced-badge">
            <TagOutlined className="bs-underpriced-icon" />
            <span>UNDERPRICED</span>
          </div>
        )}
        {signal.viewedAt && (
          <div className="bs-card-viewed-badge">
            <EyeOutlined style={{ fontSize: 11 }} />
            Viewed
          </div>
        )}
        <img
          className="bs-card-image"
          src={signal.thumbnailUrl || (signal.photos && signal.photos.length > 0 ? signal.photos[0] : null) || "https://via.placeholder.com/300x200/2a2a2a/666?text=Car"}
          alt={`${signal.year} ${signal.make} ${signal.model}`}
          onError={(e) => {
            // Try next photo in array if available
            if (signal.photos && signal.photos.length > 1) {
              const currentIndex = signal.photos.indexOf(e.target.src);
              const nextIndex = currentIndex + 1;
              if (nextIndex < signal.photos.length) {
                e.target.src = signal.photos[nextIndex];
                return;
              }
            }
            // Fallback to placeholder
            e.target.src = "https://via.placeholder.com/300x200/2a2a2a/666?text=Car";
          }}
        />
      </div>

      {/* Card Content */}
      <div className="bs-card-content">
        {/* Car Model */}
        <div className="bs-card-model">
          {signal.year} {signal.make} {signal.model} {signal.trim}
        </div>

        {/* Listed Time Tag + Site Icon */}
        <div className="bs-card-listed-time">
          <span className="bs-listed-tag">
            {signal.listingSite === "Facebook" && (
              <img src={facebookLogo} alt="" className="bs-listed-site-icon" aria-hidden />
            )}
            {signal.listingSite === "OfferUp" && (
              <img src={offerUpLogo} alt="" className="bs-listed-site-icon" aria-hidden />
            )}
            {signal.listingSite === "Craigslist" && (
              <span className="bs-listed-site-icon bs-listed-site-cl" aria-hidden>CL</span>
            )}
            {timeAgo}
          </span>
        </div>

        {/* Mileage */}
        <div className="bs-card-meta">
          {signal.mileage.toLocaleString()} mi
        </div>

        {/* Location */}
        <div className="bs-card-location">
          <EnvironmentOutlined className="bs-location-icon" />
          {signal.location}
          {signal.distanceMi != null && (
            <>
              <span className="bs-card-location-dot">•</span>
              <span className="bs-card-distance">
                {Number(signal.distanceMi) === Math.round(signal.distanceMi)
                  ? `${signal.distanceMi}`
                  : `${Number(signal.distanceMi).toFixed(1)}`}
                mi Away
              </span>
            </>
          )}
        </div>

        {/* Market Snapshot */}
        <div className="bs-market-snapshot">
          <div className="bs-snapshot-header">Market Snapshot</div>
          <div className="bs-snapshot-stats">
            <div className="bs-snapshot-stat">
              <span className="bs-stat-value">{signal.activeCount ?? 0}</span>
              <span className="bs-stat-label">Active</span>
            </div>
            <div className="bs-snapshot-stat">
              <span className="bs-stat-value">{signal.soldCount ?? 0}</span>
              <span className="bs-stat-label">Sold</span>
            </div>
            <div className="bs-snapshot-stat">
              <span className="bs-stat-value">{signal.soldDom ?? 0}D</span>
              <span className="bs-stat-label">Avg DOM</span>
            </div>
          </div>
        </div>

        {/* Price Opportunity — full (Ask + Hooptie + Above Ask) for recommended/underpriced */}
        <div className={`bs-price-opportunity ${!showFullPriceOpportunity ? "bs-price-opportunity-ask-only" : ""}`}>
          <div className="bs-opportunity-header">Price Opportunity</div>
          <div className="bs-opportunity-prices">
            <div className="bs-opportunity-price">
              <span className="bs-price-label">Ask:</span>
              <span className="bs-price-value">{askingDisplay}</span>
            </div>
            {showFullPriceOpportunity && (
              <div className="bs-opportunity-price">
                <span className="bs-price-label">Hooptie Price:</span>
                <span className="bs-price-value bs-price-value-green">{hooptieDisplay}</span>
              </div>
            )}
          </div>
          {showFullPriceOpportunity && (
            <div className="bs-price-below-ask">
              {priceDiff != null ? (
                <>
                  <ArrowUpOutlined className="bs-arrow-icon" />
                  {priceDiffDisplay}
                </>
              ) : (
                <span>{priceDiffDisplay}</span>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bs-card-actions">
          <Button
            className="bs-view-condition-report-btn"
            loading={conditionReportLoading}
            onClick={async (e) => {
              e.stopPropagation();

              // Open a blank window synchronously so mobile browsers (iOS Safari, Chrome) don't block it as a popup.
              // We then point that window at the blob URL once the PDF has loaded.
              const previewWindow = window.open("", "_blank");

              if (!previewWindow) {
                message.error("Please allow pop-ups to view the condition report.");
                return;
              }

              setConditionReportLoading(true);
              try {
                const pdfBlob = await fetchConditionReportPdf(signal.id);
                const url = URL.createObjectURL(pdfBlob);

                previewWindow.location.href = url;

                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (err) {
                const status = err.response?.status;
                let detail = err.message;
                const data = err.response?.data;
                if (data && typeof data.text === "function") {
                  try {
                    const text = await data.text();
                    const parsed = JSON.parse(text);
                    if (parsed.detail) detail = parsed.detail;
                  } catch (_) {}
                } else if (data && typeof data === "object" && data.detail) {
                  detail = data.detail;
                }
                if (status === 404) {
                  message.warning(detail || "No condition report available for this listing.");
                } else {
                  message.error(detail || "Failed to load condition report.");
                }

                try {
                  previewWindow.close();
                } catch (_) {}
              } finally {
                setConditionReportLoading(false);
              }
            }}
          >
            View Condition Report
          </Button>
          <div className="bs-card-buttons">
            <Button
              className="bs-view-listing-btn"
              onClick={(e) => {
                e.stopPropagation();
                onMarkViewed?.();
                if (signal.vdpUrl) {
                  window.open(signal.vdpUrl, '_blank');
                }
              }}
            >
              View Listing
            </Button>
            <Button
              className="bs-view-insights-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (onViewInsights) {
                  onViewInsights();
                } else {
                  onClick();
                }
              }}
            >
              View Insights
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
//test
